// src/pages/ShoppingCart.jsx
import React from 'react';
import { WebLayout } from 'components/Layout';
import { useMyCart } from '../api/getMyCart';
import { useRemoveFromCart } from '../api/removeFromCart';
import { useIncreaseQuantity } from '../api/increaseQuantity';
import { useDecreaseQuantity } from '../api/decreaseQuantity';
import { useClearCart } from '../api/clearCart';
import { Trash2, Plus, Minus, ShoppingCart as CartIcon } from 'react-feather';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export const ShoppingCart = () => {
  const { data, isLoading } = useMyCart();
  const removeMutation = useRemoveFromCart();
  const increaseMutation = useIncreaseQuantity();
  const decreaseMutation = useDecreaseQuantity();
  const clearMutation = useClearCart();
  const navigate = useNavigate();
  const cart = data?.cart;

  const handleRemove = async (productId) => {
    await removeMutation.mutateAsync(productId);
  };

  const handleIncrease = async (productId) => {
    await increaseMutation.mutateAsync(productId);
  };

  const handleDecrease = async (productId) => {
    await decreaseMutation.mutateAsync(productId);
  };

  const handleClear = async () => {
    await clearMutation.mutateAsync();
  };

  if (isLoading) {
    return (
      <WebLayout>
        <div className="flex justify-center py-32 text-gray-500">Loading cart...</div>
      </WebLayout>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <WebLayout>
        <div className="flex flex-col items-center justify-center py-32 text-gray-600">
          <CartIcon size={48} className="text-[#AC3F1E] mb-4" />
          <p className="text-lg font-medium">Your cart is empty.</p>
        </div>
      </WebLayout>
    );
  }

  return (
    <WebLayout>
      <section className=" mx-auto bg-white py-16 px-6 sm:px-12 lg:px-24 min-h-screen">
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* === Left: Cart Items === */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md overflow-hidden p-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between mb-10"
            >
              <h1 className="text-3xl font-semibold text-[#AC3F1E]">Shopping Cart</h1>
              <button
                onClick={handleClear}
                disabled={clearMutation.isLoading}
                className="text-sm text-red-600 hover:text-red-800 flex items-center gap-2"
              >
                <Trash2 size={16} /> Clear Cart
              </button>
            </motion.div>
            {cart.items.map((item) => (
              <motion.div
                key={item.product._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b py-3 last:border-none"
              >
                {/* Product Info */}
                <div className="flex items-center gap-3 sm:gap-4">
                  <img
                    src={item.product.image}
                    alt={item.product.title}
                    className="w-14 h-14 sm:w-16 sm:h-16 object-cover rounded-md border"
                  />
                  <div className="max-w-[150px] sm:max-w-none">
                    <h3 className="font-medium text-gray-800 text-sm sm:text-base">
                      {item.product.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {item.product.currency} {item.price.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Quantity Controls */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleDecrease(item.product._id)}
                    className="bg-gray-200 hover:bg-gray-300 p-2 rounded-md"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="font-semibold">{item.quantity}</span>
                  <button
                    onClick={() => handleIncrease(item.product._id)}
                    className="bg-gray-200 hover:bg-gray-300 p-2 rounded-md"
                  >
                    <Plus size={14} />
                  </button>
                </div>

                {/* Subtotal + Remove */}
                <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6">
                  <p className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                    {(item.price * item.quantity).toLocaleString()} {item.product.currency}
                  </p>
                  <button
                    onClick={() => handleRemove(item.product._id)}
                    className="text-gray-500 hover:text-red-600 transition"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* === Right: Order Summary === */}
          <div className="bg-white rounded-lg shadow-md p-6 h-fit">
            <h2 className="text-3xl font-semibold text-[#AC3F1E] mb-6">Order Summary</h2>

            {/* Summary Details */}
            <div className="space-y-3 text-gray-700">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>
                  {cart.subtotal.toLocaleString()} {cart.currency}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span>Delivery</span>
                <span className="text-gray-500">Calculated at checkout</span>
              </div>

              <div className="border-t border-gray-200 my-3"></div>

              <div className="flex justify-between font-semibold text-gray-800 text-base">
                <span>Total</span>
                <span>
                  {cart.subtotal.toLocaleString()} {cart.currency}
                </span>
              </div>
            </div>

            {/* Checkout Button */}
            <button
              onClick={() => navigate('/orders/checkout')}
              className="w-full mt-6 bg-[#AC3F1E] text-white py-3 rounded-md uppercase tracking-wide font-medium hover:opacity-90 transition"
            >
              Continue to Checkout
            </button>
          </div>
        </div>
      </section>
    </WebLayout>
  );
};
